import { supabase } from '@/integrations/supabase/client';
import { Course, Module } from '@/types/courseTypes';

/**
 * Sync Service for migrating static course data to database
 * One-time use service for populating database with existing static course content
 */

interface SyncResult {
  success: boolean;
  coursesSynced: number;
  modulesSynced: number;
  errors: string[];
}

/**
 * Sync a single course to the database
 */
const syncCourseToDatabase = async (
  course: any,
  category: string
): Promise<{ success: boolean; moduleCount: number; error?: string }> => {
  try {
    // Upsert course
    const { error: courseError } = await supabase
      .from('courses')
      .upsert({
        id: course.id,
        title: course.title,
        description: course.description || '',
        category: category,
        level: course.level || 'intermediate',
        duration_hours: course.estimatedHours || course.durationHours || 0,
        total_modules: course.modules?.length || 0,
        prerequisites: course.prerequisites || [],
        learning_outcomes: course.learningOutcomes || course.learning_outcomes || [],
        tags: course.tags || [],
        is_active: true,
        image_url: course.image || course.imageSrc || null
      }, {
        onConflict: 'id'
      });

    if (courseError) {
      throw new Error(`Course upsert failed: ${courseError.message}`);
    }

    // Sync modules
    let modulesCount = 0;
    if (course.modules && Array.isArray(course.modules)) {
      for (const module of course.modules) {
        await syncModuleToDatabase(course.id, module);
        modulesCount++;
      }
    }

    return { success: true, moduleCount: modulesCount };
  } catch (error) {
    return {
      success: false,
      moduleCount: 0,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Sync a single module to the database
 */
const syncModuleToDatabase = async (courseId: string, module: any): Promise<void> => {
  // Upsert course_modules entry
  const { error: moduleError } = await supabase
    .from('course_modules')
    .upsert({
      course_id: courseId,
      module_id: module.id,
      module_title: module.title,
      module_order: module.order || 0,
      is_enabled: true,
      estimated_duration: parseInt(module.duration || module.estimatedTime || '30'),
      difficulty_level: module.difficulty || 'intermediate',
      prerequisites: module.prerequisites || []
    }, {
      onConflict: 'course_id,module_id'
    });

  if (moduleError) {
    throw new Error(`Module upsert failed: ${moduleError.message}`);
  }

  // Upsert module_content entry
  const { error: contentError } = await supabase
    .from('module_content')
    .upsert({
      course_id: courseId,
      module_id: module.id,
      content: module.content || '',
      objectives: module.objectives || module.learningObjectives || [],
      prerequisites: module.prerequisites || [],
      key_concepts: module.keyConcepts || [],
      video_url: module.videoUrl || null,
      difficulty_level: module.difficulty || 'intermediate',
      learning_outcomes: module.learningOutcomes || []
    }, {
      onConflict: 'course_id,module_id'
    });

  if (contentError) {
    throw new Error(`Content upsert failed: ${contentError.message}`);
  }
};

/**
 * Sync all static courses from a specific category to database
 */
export const syncCategoryToDatabase = async (
  categoryId: string,
  courses: any[]
): Promise<SyncResult> => {
  const result: SyncResult = {
    success: true,
    coursesSynced: 0,
    modulesSynced: 0,
    errors: []
  };

  for (const course of courses) {
    const syncResult = await syncCourseToDatabase(course, categoryId);
    
    if (syncResult.success) {
      result.coursesSynced++;
      result.modulesSynced += syncResult.moduleCount;
    } else {
      result.success = false;
      result.errors.push(`${course.id}: ${syncResult.error}`);
    }
  }

  return result;
};

/**
 * Sync all static courses to database with progress callback
 */
export const syncAllCoursesWithProgress = async (
  onProgress?: (progress: Partial<{
    status: 'syncing' | 'completed' | 'error';
    totalCourses: number;
    syncedCourses: number;
    totalModules: number;
    syncedModules: number;
    currentCategory: string;
    categoryResults: Record<string, SyncResult>;
  }>) => void
): Promise<{
  totalCourses: number;
  totalModules: number;
  errors: string[];
  categoryResults: Record<string, SyncResult>;
}> => {
  const categoryResults: Record<string, SyncResult> = {};
  let totalCourses = 0;
  let totalModules = 0;
  const allErrors: string[] = [];

  onProgress?.({ 
    status: 'syncing', 
    currentCategory: 'Starting sync...', 
    syncedCourses: 0, 
    syncedModules: 0 
  });

  // Helper function to sync a category with progress
  const syncCategory = async (categoryId: string, importPath: string, exportName: string) => {
    try {
      onProgress?.({ currentCategory: categoryId });
      
      const module = await import(importPath);
      const courses = module[exportName];
      if (courses && Array.isArray(courses)) {
        const result = await syncCategoryToDatabase(categoryId, courses);
        categoryResults[categoryId] = result;
        totalCourses += result.coursesSynced;
        totalModules += result.modulesSynced;
        allErrors.push(...result.errors.map(e => `[${categoryId}] ${e}`));
        
        onProgress?.({
          syncedCourses: totalCourses,
          syncedModules: totalModules,
          totalCourses,
          totalModules,
          categoryResults: { ...categoryResults }
        });
      }
    } catch (error) {
      const errorMsg = `[${categoryId}] Failed to load: ${error instanceof Error ? error.message : 'Unknown error'}`;
      allErrors.push(errorMsg);
      categoryResults[categoryId] = {
        success: false,
        coursesSynced: 0,
        modulesSynced: 0,
        errors: [errorMsg]
      };
    }
  };

  try {
    // Core Programming Languages
    await syncCategory('python', '@/data/pythonCourseData', 'PYTHON_COURSES');
    await syncCategory('java', '@/data/java', 'JAVA_COURSES');
    await syncCategory('javascript', '@/data/javascript', 'JAVASCRIPT_COURSES');
    await syncCategory('csharp', '@/data/csharp', 'CSHARP_COURSES');
    await syncCategory('dotnet', '@/data/dotnet', 'DOTNET_COURSES');
    await syncCategory('rust', '@/data/rust', 'RUST_COURSES');

    // Cloud Platforms
    await syncCategory('aws', '@/data/aws', 'AWS_COURSES');
    await syncCategory('azure', '@/data/azure', 'AZURE_COURSES');
    await syncCategory('gcp', '@/data/gcp', 'GCP_COURSES');

    // Data & Analytics
    await syncCategory('sql', '@/data/sql', 'SQL_COURSES');
    await syncCategory('etl', '@/data/etl', 'ETL_COURSES');
    await syncCategory('powerbi', '@/data/powerbi', 'POWERBI_COURSES');
    await syncCategory('data-science', '@/data/data-science', 'DATA_SCIENCE_COURSES');
    await syncCategory('datascience', '@/data/datascience', 'DATASCIENCE_COURSES');
    await syncCategory('dataengineering', '@/data/dataengineering', 'DATAENGINEERING_COURSES');
    await syncCategory('ml', '@/data/ml', 'ML_COURSES');

    // AI & Machine Learning
    await syncCategory('ai', '@/data/ai/courses', 'AI_COURSES');

    // DevOps & Infrastructure
    await syncCategory('devops', '@/data/devops', 'DEVOPS_COURSES');
    await syncCategory('kubernetes', '@/data/kubernetes-mastery', 'KUBERNETES_MASTERY_COURSES');
    await syncCategory('infrastructure', '@/data/infrastructure', 'INFRASTRUCTURE_COURSES');
    await syncCategory('edge-computing', '@/data/edge-computing', 'EDGE_COMPUTING_COURSES');

    // Frontend & React
    await syncCategory('react', '@/data/courses', 'COURSES');
    await syncCategory('modern-frontend', '@/data/modern-frontend', 'MODERN_FRONTEND_COURSES');
    await syncCategory('frontend-advanced', '@/data/frontend-advanced', 'FRONTEND_ADVANCED_COURSES');

    // Backend & APIs
    await syncCategory('backend', '@/data/backend', 'BACKEND_COURSES');
    await syncCategory('advanced-api', '@/data/advanced-api', 'ADVANCED_API_COURSES');
    await syncCategory('serverless', '@/data/serverless-edge', 'SERVERLESS_EDGE_COURSES');

    // Security
    await syncCategory('security', '@/data/security', 'SECURITY_COURSES');
    await syncCategory('appsec', '@/data/appsec', 'APPSEC_COURSES');

    // Testing
    await syncCategory('testing', '@/data/testing', 'SELENIUM_COURSES');
    await syncCategory('testing-modern', '@/data/testing-modern', 'TESTING_MODERN_COURSES');

    // Mobile & Cross-Platform
    await syncCategory('mobile', '@/data/mobile', 'MOBILE_COURSES');
    await syncCategory('cross-platform', '@/data/cross-platform', 'CROSS_PLATFORM_COURSES');

    // Blockchain & Web3
    await syncCategory('blockchain', '@/data/blockchain', 'BLOCKCHAIN_COURSES');
    await syncCategory('web3', '@/data/web3-development', 'WEB3_DEVELOPMENT_COURSES');

    // Graphics & Game Development
    await syncCategory('graphics', '@/data/graphics', 'GRAPHICS_COURSES');
    await syncCategory('graphics-programming', '@/data/graphics-programming', 'GRAPHICS_PROGRAMMING_COURSES');
    
    // Game Development (individual courses)
    try {
      const { UNITY_GAME_DEVELOPMENT, UNREAL_GAME_DEVELOPMENT, AR_VR_DEVELOPMENT, DESKTOP_APPLICATION_DEVELOPMENT } = await import('@/data/game-development');
      const gameDevCourses = [UNITY_GAME_DEVELOPMENT, UNREAL_GAME_DEVELOPMENT, AR_VR_DEVELOPMENT, DESKTOP_APPLICATION_DEVELOPMENT];
      const result = await syncCategoryToDatabase('game-development', gameDevCourses);
      categoryResults['game-development'] = result;
      totalCourses += result.coursesSynced;
      totalModules += result.modulesSynced;
      allErrors.push(...result.errors.map(e => `[game-development] ${e}`));
    } catch (error) {
      allErrors.push(`[game-development] Failed to load: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // UI/UX & Design
    await syncCategory('ui-ux', '@/data/ui-ux-design', 'UI_UX_COURSES');

    // Automation & RPA
    await syncCategory('rpa', '@/data/rpa', 'RPA_COURSES');

    // Professional Development
    await syncCategory('professional-development', '@/data/professional-development', 'PROFESSIONAL_DEVELOPMENT_COURSES');
    await syncCategory('enterprise-training', '@/data/enterprise-training', 'ENTERPRISE_TRAINING_COURSES');

    // Database
    await syncCategory('database', '@/data/database', 'DATABASE_COURSES');

    // Emerging Technologies
    await syncCategory('green-tech', '@/data/green-tech', 'GREEN_TECH_COURSES');
    await syncCategory('no-code', '@/data/no-code', 'NO_CODE_COURSES');

    // Analytics & BI
    try {
      const { TABLEAU_COURSES } = await import('@/data/tableau');
      const result = await syncCategoryToDatabase('tableau', TABLEAU_COURSES);
      categoryResults.tableau = result;
      totalCourses += result.coursesSynced;
      totalModules += result.modulesSynced;
      allErrors.push(...result.errors.map(e => `[tableau] ${e}`));
    } catch (error) {
      allErrors.push(`[tableau] Failed to load: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // SQL Phase 1 (additional SQL courses)
    try {
      const { SQL_PHASE1_COURSES } = await import('@/data/sql/phase1');
      const result = await syncCategoryToDatabase('sql', SQL_PHASE1_COURSES);
      totalCourses += result.coursesSynced;
      totalModules += result.modulesSynced;
      allErrors.push(...result.errors.map(e => `[sql-phase1] ${e}`));
    } catch (error) {
      allErrors.push(`[sql-phase1] Failed to load: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    // SQL Phase 2-6 (advanced SQL courses)
    try {
      const { SQL_ADVANCED_PHASE_COURSES } = await import('@/data/sql');
      const result = await syncCategoryToDatabase('sql', SQL_ADVANCED_PHASE_COURSES);
      totalCourses += result.coursesSynced;
      totalModules += result.modulesSynced;
      allErrors.push(...result.errors.map(e => `[sql-phase2-6] ${e}`));
    } catch (error) {
      allErrors.push(`[sql-phase2-6] Failed to load: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

  } catch (error) {
    allErrors.push(`General error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }

  onProgress?.({ 
    status: 'completed',
    totalCourses,
    totalModules,
    syncedCourses: totalCourses,
    syncedModules: totalModules
  });

  return {
    totalCourses,
    totalModules,
    errors: allErrors,
    categoryResults
  };
};

/**
 * Sync all static courses to database - Legacy function for backward compatibility
 */
export const syncAllStaticCoursesToDatabase = async (): Promise<{
  totalCourses: number;
  totalModules: number;
  errors: string[];
  categoryResults: Record<string, SyncResult>;
}> => {
  return syncAllCoursesWithProgress();
};
